// Photo Bundle — /admin/photo-bundle
//
// Upload a zip of photos + notes.txt from a tech, review everything in a
// gallery, and turn selections into WOs with photos attached.
//
// UX: single page. Top = upload form. After upload, the form collapses
// and the gallery appears below with the parsed drafts sidebar. Each
// photo can be attached to any draft with a click. "Create N WOs" fans
// out through /api/photo-bundle/{id}/create-wos.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, UploadSimple, Image as ImageIcon, MagicWand, Plus,
  CheckCircle, Warning, Trash, Paperclip, X as XIcon, House, FileText, MapPin,
  Play, Spinner, Package as PackageIcon,
} from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";

const CATEGORIES = [
  "Plumbing", "HVAC / Heat / AC", "Electrical", "Appliance",
  "Pest / Rodent", "Lock / Key / Door", "Leak / Water Damage",
  "Roof / Ceiling", "Other",
];
const PRIORITIES = ["normal", "high", "urgent"];
const PRIORITY_COLOR = {
  normal: "bg-slate-100 text-slate-700 border-slate-300",
  high: "bg-amber-100 text-amber-800 border-amber-400",
  urgent: "bg-rose-100 text-rose-800 border-rose-500",
};

export default function AdminPhotoBundle() {
  const { id } = useParams();  // when set → we're on gallery view
  const nav = useNavigate();

  // ── Upload form state ───────────────────────────────────────────
  const [file, setFile] = useState(null);
  const [notesFile, setNotesFile] = useState(null);  // optional .docx / .txt
  const [defaultAddress, setDefaultAddress] = useState("");
  const [uploading, setUploading] = useState(false);

  // ── Gallery state ───────────────────────────────────────────────
  const [bundle, setBundle] = useState(null);   // {id, photos, drafts, notes, default_property_address, status, progress}
  const [loading, setLoading] = useState(false);
  // Each assignment mirrors a draft, plus tracks which photo indices are attached.
  const [assignments, setAssignments] = useState([]);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  // Lightbox — holds {type: 'photo'|'video', idx}
  const [lightbox, setLightbox] = useState(null);

  // Poll status while the bundle is still being processed on the server.
  // We key ONLY on bundle?.id so a status mutation doesn't cancel the
  // effect mid-tick. When status flips to ready/error we refetch the
  // full bundle before touching state, so React can't discard the
  // payload via a cleanup race.
  useEffect(() => {
    if (!bundle?.id) return;
    if (bundle.status !== "processing") return;
    let stopped = false;
    let timer = null;
    const tick = async () => {
      if (stopped) return;
      try {
        const r = await api.get(`/photo-bundle/${bundle.id}/status`);
        if (stopped) return;
        if (r.data.status === "ready" || r.data.status === "error") {
          // Refetch FIRST, then land everything in one setState.
          const full = await api.get(`/photo-bundle/${bundle.id}`);
          if (stopped) return;
          hydrateBundle(full.data);
          if (r.data.status === "error") {
            toast.error(r.data.progress?.error || "Processing failed");
          }
          return;  // stop polling
        }
        // Update progress in place without touching status.
        setBundle(b => b ? { ...b, progress: r.data.progress } : b);
      } catch (e) {
        // Transient poll failure — ignore, next tick will retry.
      }
      if (!stopped) timer = setTimeout(tick, 2000);
    };
    tick();  // kick immediately
    return () => { stopped = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundle?.id]);

  const hydrateBundle = (data) => {
    setBundle(data);
    // Only build assignments once — user may already have edited them.
    setAssignments(prev => {
      if (prev.length > 0) return prev;
      const drafts = data.drafts || [];
      const photos = data.photos || [];
      const nameToIdx = new Map(photos.map((p, i) => [p.filename.toLowerCase(), i]));
      const a = drafts.map(d => ({
        ...d,
        _keep: true,
        photo_indices: (d.photo_hints || [])
          .map(h => nameToIdx.get((h || "").toLowerCase()))
          .filter(i => typeof i === "number"),
        video_indices: [],
      }));
      photos.forEach((p, pi) => {
        if (!p.unit_hint) return;
        a.forEach(as => {
          if ((as.unit || "").toUpperCase() === p.unit_hint.toUpperCase()
              && !as.photo_indices.includes(pi)) {
            as.photo_indices.push(pi);
          }
        });
      });
      return a;
    });
  };

  useEffect(() => {
    if (!id) { setBundle(null); return; }
    setLoading(true);
    api.get(`/photo-bundle/${id}`).then(r => {
      hydrateBundle(r.data);
    }).catch(e => {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Bundle not found");
    }).finally(() => setLoading(false));
  }, [id]);

  const upload = async () => {
    if (!file) { toast.error("Pick a .zip first"); return; }
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("File must be a .zip");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (notesFile) fd.append("notes_file", notesFile);
      const r = await api.post(
        `/photo-bundle/upload?default_property_address=${encodeURIComponent(defaultAddress || "")}`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000,  // 5 min — return-fast pattern usually replies in <30s
        }
      );
      toast.success(r.data.status === "processing"
        ? "Uploaded — processing in the background…"
        : `Extracted ${r.data.photos?.length ?? 0} photos`);
      nav(`/admin/photo-bundle/${r.data.id}`, { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const patchAssignment = (i, partial) => {
    setAssignments(prev => prev.map((a, idx) => idx === i ? { ...a, ...partial } : a));
  };
  const removeAssignment = (i) => {
    setAssignments(prev => prev.filter((_, idx) => idx !== i));
  };
  const addBlankAssignment = () => {
    setAssignments(prev => [...prev, {
      unit: "", title: "", description: "",
      category: "Other", priority: "normal",
      property_address: bundle?.default_property_address || "",
      photo_indices: [], _keep: true,
    }]);
  };

  const togglePhotoOnDraft = (assignmentIdx, photoIdx) => {
    setAssignments(prev => prev.map((a, idx) => {
      if (idx !== assignmentIdx) return a;
      const has = a.photo_indices.includes(photoIdx);
      return {
        ...a,
        photo_indices: has
          ? a.photo_indices.filter(p => p !== photoIdx)
          : [...a.photo_indices, photoIdx],
      };
    }));
  };

  const createAll = async () => {
    const keep = assignments.filter(a => a._keep !== false);
    if (!keep.length) { toast.error("Nothing to create"); return; }
    const bad = keep.find(a => !(a.property_address || "").trim() || !(a.title || "").trim());
    if (bad) { toast.error("Every WO needs a property address and a title"); return; }
    setCreating(true);
    try {
      const payload = keep.map(({ _keep, photo_hints, ...rest }) => rest);
      const r = await api.post(`/photo-bundle/${bundle.id}/create-wos`,
        { assignments: payload });
      setResult(r.data);
      toast.success(`Created ${r.data.created_count} work order${r.data.created_count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Create failed");
    } finally { setCreating(false); }
  };

  const photoUrlFor = (idx) => {
    const t = localStorage.getItem("access_token");
    return `${process.env.REACT_APP_BACKEND_URL}/api/photo-bundle/${bundle.id}/photo/${idx}?auth=${encodeURIComponent(t || "")}`;
  };
  const videoUrlFor = (idx) => {
    const t = localStorage.getItem("access_token");
    return `${process.env.REACT_APP_BACKEND_URL}/api/photo-bundle/${bundle.id}/video/${idx}?auth=${encodeURIComponent(t || "")}`;
  };

  // Set of photo indices attached anywhere → to dim "already used" in the pool.
  const attachedSet = useMemo(() => {
    const s = new Set();
    assignments.forEach(a => a.photo_indices?.forEach(i => s.add(i)));
    return s;
  }, [assignments]);
  const attachedVideoSet = useMemo(() => {
    const s = new Set();
    assignments.forEach(a => a.video_indices?.forEach(i => s.add(i)));
    return s;
  }, [assignments]);

  // ── Upload page (no bundle id in URL) ───────────────────────────
  if (!id) {
    return (
      <AdminShell title="Photo Bundle">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          <div className="rounded-lg overflow-hidden bg-gradient-to-br from-slate-900 via-fuchsia-900 to-slate-950 text-white p-6">
            <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-300 mb-1">
              Field Radar · Evidence Intake
            </div>
            <h1 className="font-display font-black text-3xl leading-none tracking-tight mb-2">
              Zip → Photos + WOs
            </h1>
            <p className="text-sm text-white/70 max-w-2xl">
              Upload a zip from a tech's phone (photos + a notes.txt if they wrote one).
              We extract everything, guess units from filenames, parse the notes into
              draft WOs, and drop you in a gallery to review before creating anything.
            </p>
          </div>

          <section className="bg-white border-2 border-slate-200 rounded-lg p-4 space-y-3"
            data-testid="pb-upload-panel">
            <div className="flex items-center gap-2">
              <UploadSimple size={16} className="text-slate-500"/>
              <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">
                Pick a zip
              </div>
            </div>
            <label
              className={`block border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${
                file ? "border-fuchsia-400 bg-fuchsia-50" : "border-slate-300 bg-slate-50 hover:bg-slate-100"
              }`}
              data-testid="pb-upload-drop">
              <input type="file" accept=".zip,application/zip"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="pb-upload-input"/>
              {file ? (
                <div className="space-y-1">
                  <div className="font-black text-sm text-fuchsia-900 truncate">{file.name}</div>
                  <div className="text-[10px] text-fuchsia-700">
                    {(file.size / 1024 / 1024).toFixed(1)} MB · click to change
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <UploadSimple size={28} className="text-slate-400 mx-auto"/>
                  <div className="text-sm font-black text-slate-600">Click to select a .zip</div>
                  <div className="text-[10px] text-slate-500">
                    Photos, videos + optional notes · up to 200 MB
                  </div>
                </div>
              )}
            </label>

            {/* Optional external notes file — Kraaft exports the chat as a
                separate .docx from the photo zip; users can drop it here. */}
            <label
              className={`block border-2 border-dashed rounded-md p-3 text-center cursor-pointer transition ${
                notesFile ? "border-indigo-400 bg-indigo-50" : "border-slate-300 bg-white hover:bg-slate-50"
              }`}
              data-testid="pb-notes-drop">
              <input type="file" accept=".docx,.txt,.md,.rtf"
                onChange={e => setNotesFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="pb-notes-input"/>
              {notesFile ? (
                <div className="flex items-center justify-center gap-2 text-xs">
                  <FileText size={14} className="text-indigo-700"/>
                  <span className="font-black text-indigo-900 truncate">{notesFile.name}</span>
                  <span className="text-[10px] text-indigo-600">({(notesFile.size/1024).toFixed(0)} KB)</span>
                  <button type="button"
                    onClick={(e) => { e.preventDefault(); setNotesFile(null); }}
                    className="text-rose-500 hover:text-rose-700">
                    <XIcon size={12}/>
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                  <FileText size={14}/>
                  <span>Optional: chat export or notes file (.docx / .txt)</span>
                </div>
              )}
            </label>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 whitespace-nowrap">
                Building context (optional)
              </label>
              <input
                value={defaultAddress}
                onChange={e => setDefaultAddress(e.target.value)}
                placeholder="e.g. Krafft Rd"
                data-testid="pb-upload-default-address"
                className="flex-1 h-9 px-2 border-2 border-slate-300 text-sm"/>
              <button
                onClick={upload}
                disabled={uploading || !file}
                data-testid="pb-upload-btn"
                className={`h-10 px-4 inline-flex items-center gap-1.5 font-black uppercase text-[11px] tracking-widest whitespace-nowrap ${
                  uploading || !file
                    ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                    : "bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                }`}>
                <MagicWand size={12}/> {uploading ? "Extracting…" : "Extract"}
              </button>
            </div>
            <div className="text-[10px] text-slate-400">
              Tip — name files like <code className="font-mono bg-slate-100 px-1">Unit3A_kitchen.jpg</code> and we'll auto-link them to their WO.
            </div>
          </section>
        </div>
      </AdminShell>
    );
  }

  // ── Gallery view ────────────────────────────────────────────────
  if (loading) {
    return <AdminShell title="Photo Bundle"><div className="p-10 text-center text-slate-500">Loading bundle…</div></AdminShell>;
  }
  if (!bundle) {
    return <AdminShell title="Photo Bundle"><div className="p-10 text-center text-slate-500">Bundle not found</div></AdminShell>;
  }

  const keepCount = assignments.filter(a => a._keep !== false).length;

  return (
    <AdminShell title="Photo Bundle">
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => nav("/admin/photo-bundle")}
            data-testid="pb-back"
            className="h-8 px-3 inline-flex items-center gap-1 border-2 border-slate-300 hover:bg-slate-50 font-black uppercase text-[10px] tracking-widest">
            <ArrowLeft size={11}/> New bundle
          </button>
          <div className="text-xs text-slate-500 truncate">
            {bundle.photos.length} photo{bundle.photos.length === 1 ? "" : "s"}
            {bundle.default_property_address && <> · <House size={11} className="inline"/> {bundle.default_property_address}</>}
          </div>
        </div>

        {/* Processing banner — visible while the background task extracts. */}
        {bundle.status === "processing" && (
          <ProcessingBanner progress={bundle.progress || {}}/>
        )}
        {bundle.status === "error" && (
          <div className="rounded-lg border-2 border-rose-400 bg-rose-50 p-3 text-sm text-rose-900" data-testid="pb-error-banner">
            <Warning weight="fill" className="inline mr-1" size={14}/>
            Processing failed: {bundle.progress?.error || "unknown error"}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Gallery (2 cols) */}
          <div className="lg:col-span-2 space-y-4">
            <section className="bg-white border-2 border-slate-200 rounded-lg p-4" data-testid="pb-gallery">
              <div className="flex items-center gap-2 mb-3">
                <ImageIcon size={14} className="text-slate-500"/>
                <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">
                  Gallery · {bundle.photos.length} photo{bundle.photos.length === 1 ? "" : "s"}
                  {(bundle.videos?.length || 0) > 0 && <> · {bundle.videos.length} video{bundle.videos.length === 1 ? "" : "s"}</>}
                </div>
                <div className="ml-auto text-[10px] text-slate-400 italic">
                  Click a tile to zoom · use draft cards to attach
                </div>
              </div>
              {bundle.photos.length === 0 && (bundle.videos?.length || 0) === 0 ? (
                <div className="text-sm text-slate-500 py-8 text-center">
                  {bundle.status === "processing" ? "Waiting for extraction to finish…" : "No photos or videos in this bundle"}
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {bundle.photos.map((p, i) => (
                    <button key={`p${i}`}
                      onClick={() => setLightbox({ type: "photo", idx: i })}
                      data-testid={`pb-photo-${i}`}
                      title={p.geo_address ? `GPS: ${p.geo_address}` : p.filename}
                      className={`relative aspect-square rounded overflow-hidden border-2 group ${
                        attachedSet.has(i) ? "border-emerald-500" : "border-slate-200 hover:border-slate-400"
                      }`}>
                      <img src={photoUrlFor(i)} alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"/>
                      {p.unit_hint && (
                        <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-black/70 text-white text-[9px] font-black uppercase tracking-wider rounded">
                          {p.unit_hint}
                        </div>
                      )}
                      {attachedSet.has(i) && (
                        <div className="absolute top-1 right-1 h-5 w-5 bg-emerald-500 text-white rounded-full flex items-center justify-center">
                          <CheckCircle size={11} weight="fill"/>
                        </div>
                      )}
                      {p.geo_address && (
                        <div className="absolute bottom-0 inset-x-0 px-1.5 py-1 bg-gradient-to-t from-black/85 to-transparent text-white text-[9px] font-black leading-tight truncate flex items-center gap-0.5">
                          <MapPin size={9} weight="fill" className="text-cyan-300 flex-shrink-0"/>
                          <span className="truncate">{p.geo_address.split(",")[0]}</span>
                        </div>
                      )}
                    </button>
                  ))}
                  {(bundle.videos || []).map((v, i) => (
                    <button key={`v${i}`}
                      onClick={() => setLightbox({ type: "video", idx: i })}
                      data-testid={`pb-video-${i}`}
                      title={v.filename}
                      className={`relative aspect-square rounded overflow-hidden border-2 border-slate-200 hover:border-slate-400 group ${
                        attachedVideoSet.has(i) ? "!border-indigo-500" : ""
                      }`}>
                      {/* Native <video> shows the poster frame with preload=metadata.
                          No server-side ffmpeg needed for MVP. */}
                      <video
                        src={videoUrlFor(i)}
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover bg-slate-900"/>
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="h-8 w-8 rounded-full bg-black/60 flex items-center justify-center">
                          <Play size={14} weight="fill" className="text-white ml-0.5"/>
                        </div>
                      </div>
                      <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-indigo-600/90 text-white text-[9px] font-black uppercase tracking-wider rounded">
                        Video
                      </div>
                      {v.unit_hint && (
                        <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-black/70 text-white text-[9px] font-black uppercase tracking-wider rounded">
                          {v.unit_hint}
                        </div>
                      )}
                      {attachedVideoSet.has(i) && (
                        <div className="absolute bottom-1 right-1 h-5 w-5 bg-indigo-500 text-white rounded-full flex items-center justify-center">
                          <CheckCircle size={11} weight="fill"/>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* Notes */}
            {bundle.notes && (
              <section className="bg-white border-2 border-slate-200 rounded-lg p-4" data-testid="pb-notes">
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={14} className="text-slate-500"/>
                  <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">
                    Notes from the tech
                  </div>
                </div>
                <pre className="text-xs font-mono whitespace-pre-wrap text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 max-h-64 overflow-auto">
                  {bundle.notes}
                </pre>
              </section>
            )}
          </div>

          {/* Draft assignments (1 col) */}
          <div className="lg:col-span-1">
            <section className="bg-white border-2 border-slate-200 rounded-lg p-4 sticky top-4" data-testid="pb-drafts">
              <div className="flex items-center gap-2 mb-3">
                <MagicWand size={14} className="text-fuchsia-500"/>
                <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">
                  Draft WOs · {keepCount}
                </div>
                <button
                  onClick={addBlankAssignment}
                  data-testid="pb-add-blank"
                  className="ml-auto text-[10px] text-fuchsia-700 hover:text-fuchsia-900 font-black uppercase tracking-widest">
                  + Add blank
                </button>
              </div>

              {assignments.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-500">
                  {bundle.notes
                    ? "AI didn't find WOs in the notes — click + Add blank to create one manually."
                    : "No notes.txt in the zip — click + Add blank to create a WO manually."}
                </div>
              ) : (
                <div className="space-y-3 max-h-[calc(100vh-260px)] overflow-y-auto pr-1">
                  {assignments.map((a, i) => (
                    <div key={i}
                      data-testid={`pb-draft-${i}`}
                      className={`border-2 rounded p-2 ${
                        a._keep === false ? "border-slate-200 bg-slate-50 opacity-50" : "border-slate-300"
                      }`}>
                      <div className="grid grid-cols-2 gap-1 mb-1">
                        <input list="known-addresses" autoComplete="off"
                          value={a.property_address}
                          onChange={e => patchAssignment(i, { property_address: e.target.value })}
                          placeholder="Address"
                          data-testid={`pb-draft-address-${i}`}
                          className="col-span-2 h-8 px-2 border-2 border-slate-300 text-xs"/>
                        <input
                          value={a.unit}
                          onChange={e => patchAssignment(i, { unit: e.target.value })}
                          placeholder="Unit"
                          data-testid={`pb-draft-unit-${i}`}
                          className="h-8 px-2 border-2 border-slate-300 text-xs text-center font-black"/>
                        <select
                          value={a.priority}
                          onChange={e => patchAssignment(i, { priority: e.target.value })}
                          data-testid={`pb-draft-priority-${i}`}
                          className={`h-8 px-1 border-2 text-[10px] font-black uppercase ${PRIORITY_COLOR[a.priority] || "bg-white border-slate-300"}`}>
                          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <select
                          value={a.category}
                          onChange={e => patchAssignment(i, { category: e.target.value })}
                          data-testid={`pb-draft-category-${i}`}
                          className="col-span-2 h-8 px-1 border-2 border-slate-300 text-[10px] bg-white">
                          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <input
                        value={a.title}
                        onChange={e => patchAssignment(i, { title: e.target.value })}
                        placeholder="Title"
                        data-testid={`pb-draft-title-${i}`}
                        className="w-full h-8 px-2 border-2 border-slate-300 text-xs font-black mb-1"/>
                      <textarea
                        value={a.description}
                        onChange={e => patchAssignment(i, { description: e.target.value })}
                        placeholder="Description"
                        rows={2}
                        data-testid={`pb-draft-description-${i}`}
                        className="w-full px-2 py-1 border-2 border-slate-300 text-[11px] resize-y mb-1"/>

                      {/* Attached photos */}
                      <div className="flex flex-wrap gap-1 items-center min-h-[24px]"
                        data-testid={`pb-draft-photos-${i}`}>
                        <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 inline-flex items-center gap-1">
                          <Paperclip size={9}/> {a.photo_indices.length}
                        </div>
                        {a.photo_indices.map(pi => (
                          <button key={pi}
                            onClick={() => togglePhotoOnDraft(i, pi)}
                            className="relative w-8 h-8 rounded overflow-hidden border border-emerald-500 group">
                            <img src={photoUrlFor(pi)} alt="" className="w-full h-full object-cover"/>
                            <div className="absolute inset-0 bg-rose-500/0 group-hover:bg-rose-500/80 flex items-center justify-center transition">
                              <XIcon size={11} className="text-white opacity-0 group-hover:opacity-100"/>
                            </div>
                          </button>
                        ))}
                        <button
                          onClick={() => {
                            // Open a mini picker — just cycle through unattached photos.
                            // Simple approach: attach the first unattached photo.
                            const first = bundle.photos.findIndex((_, pi) => !a.photo_indices.includes(pi));
                            if (first === -1) toast.info("No unattached photos left");
                            else togglePhotoOnDraft(i, first);
                          }}
                          className="h-6 w-6 flex items-center justify-center border-2 border-dashed border-slate-300 hover:border-fuchsia-500 hover:text-fuchsia-500 rounded text-slate-400">
                          <Plus size={10}/>
                        </button>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <button
                          onClick={() => patchAssignment(i, { _keep: a._keep === false })}
                          data-testid={`pb-draft-skip-${i}`}
                          className={`h-6 px-2 text-[9px] font-black uppercase tracking-widest ${
                            a._keep === false
                              ? "border-2 border-slate-300 text-slate-500 bg-white"
                              : "bg-emerald-100 text-emerald-800 border-2 border-emerald-400"
                          }`}>
                          {a._keep === false ? "Skipped" : "Keeping"}
                        </button>
                        <button
                          onClick={() => removeAssignment(i)}
                          data-testid={`pb-draft-drop-${i}`}
                          className="ml-auto h-6 w-6 flex items-center justify-center text-rose-500 hover:bg-rose-50">
                          <Trash size={11}/>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={createAll}
                disabled={creating || !keepCount}
                data-testid="pb-create-btn"
                className={`mt-3 w-full h-10 inline-flex items-center justify-center gap-1.5 font-black uppercase text-[11px] tracking-widest ${
                  creating || !keepCount
                    ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                    : "bg-emerald-600 hover:bg-emerald-700 text-white"
                }`}>
                <Plus size={12}/> {creating ? "Creating…" : `Create ${keepCount} WO${keepCount === 1 ? "" : "s"}`}
              </button>

              {result && (
                <div className="mt-3 p-2 bg-emerald-50 border-2 border-emerald-400 rounded text-xs"
                  data-testid="pb-result">
                  <div className="font-black text-emerald-900 mb-1">
                    ✓ {result.created_count} created
                  </div>
                  {result.created?.map((c, i) => (
                    <div key={i} className="text-[11px] text-emerald-800 truncate">
                      · Unit {c.unit || "—"} · {c.title} <span className="text-emerald-600">({c.photo_count} pic)</span>
                    </div>
                  ))}
                  {result.errors?.length > 0 && (
                    <div className="mt-1 text-rose-700 text-[11px]">
                      <Warning size={10} className="inline"/> {result.errors.length} skipped
                    </div>
                  )}
                  <a href="/admin/jobs-board"
                    data-testid="pb-goto-board"
                    className="mt-1 inline-block text-[10px] font-black uppercase tracking-widest text-emerald-800 hover:text-emerald-900 underline">
                    Open Jobs Board →
                  </a>
                </div>
              )}
            </section>
          </div>
        </div>

        {/* Lightbox — handles both photos + videos */}
        {lightbox !== null && (() => {
          const isVideo = lightbox.type === "video";
          const item = isVideo ? bundle.videos[lightbox.idx] : bundle.photos[lightbox.idx];
          if (!item) return null;
          return (
            <div
              className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
              onClick={() => setLightbox(null)}
              data-testid="pb-lightbox">
              <button
                onClick={() => setLightbox(null)}
                className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded">
                <XIcon size={24}/>
              </button>
              {isVideo ? (
                <video
                  src={videoUrlFor(lightbox.idx)}
                  controls
                  autoPlay
                  playsInline
                  className="max-w-full max-h-full"
                  onClick={e => e.stopPropagation()}/>
              ) : (
                <img
                  src={photoUrlFor(lightbox.idx)}
                  alt=""
                  className="max-w-full max-h-full object-contain"
                  onClick={e => e.stopPropagation()}/>
              )}
              <div className="absolute bottom-4 left-4 right-4 text-center text-white text-xs space-y-1">
                <div>{item.filename}</div>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  {item.unit_hint && (
                    <span className="px-2 py-0.5 bg-white/20 rounded font-black uppercase tracking-wider">
                      Unit {item.unit_hint}
                    </span>
                  )}
                  {item.geo_address && (
                    <span className="px-2 py-0.5 bg-cyan-500/30 rounded font-black inline-flex items-center gap-1">
                      <MapPin size={11} weight="fill"/> {item.geo_address}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </AdminShell>
  );
}

// ── Processing banner — shown while the backend background task runs ─
// Progress fields ship on the bundle doc via /status polling. Percentage
// derived from photos_processed / photos_total; phase copy is friendly
// ("Extracting evidence…", "Reading the chat…").
const PHASE_LABEL = {
  queued: "Queued — starting up…",
  extract: "Extracting photos + reverse-geocoding…",
  parse: "Reading the chat + drafting work orders…",
  ready: "Ready",
};

function ProcessingBanner({ progress }) {
  const total = progress?.photos_total ?? 0;
  const done = progress?.photos_processed ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : (progress?.phase === "parse" ? 90 : 5);
  const phase = progress?.phase || "queued";
  return (
    <div
      data-testid="pb-processing-banner"
      className="rounded-lg border-2 border-indigo-400 bg-gradient-to-br from-indigo-50 to-fuchsia-50 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Spinner size={16} weight="bold" className="text-indigo-600 animate-spin"/>
        <div className="font-black uppercase text-[11px] tracking-widest text-indigo-800">
          Processing bundle
        </div>
        <div className="ml-auto text-[11px] font-black text-indigo-700 tabular-nums">
          {pct}%
        </div>
      </div>
      <div className="h-2 bg-indigo-100 rounded overflow-hidden mb-2">
        <div
          className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 transition-all duration-500"
          style={{ width: `${pct}%` }}/>
      </div>
      <div className="text-xs text-indigo-900" data-testid="pb-processing-phase">
        {PHASE_LABEL[phase] || phase}
        {total > 0 && phase === "extract" && (
          <span className="text-indigo-600 ml-1">· {done} of {total} photos</span>
        )}
      </div>
      <div className="text-[10px] text-indigo-500 mt-1 italic">
        You can leave this page — the bundle will keep processing. Come back anytime.
      </div>
    </div>
  );
}

